<script setup lang="ts">
  import { useApiNew } from '@/shared/composables/useApi'
  import { useConvertUsd } from '@/shared/composables/useConvertUsd'
  import { getEntryById } from '@/shared/composables/useEntry'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { useIsHolder } from '@/shared/composables/useIsHolder'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { useScrollToTop } from '@/shared/composables/useScroll'
  import { documents } from '@/shared/constants/documents.constants'
  import {
    defaultVisitor,
    INBOUND_ADDON_CODE,
    INBOUND_ADDON_KEYS,
    paymentMethods,
    STATE_LIST_INS_KEY,
    viewsMap,
  } from '@/shared/constants/other.constants'
  import {
    emailResponse,
    inboundCheckResponse,
  } from '@/shared/constants/validationMessages.constants'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { PaymentType } from '@/shared/types/response.types'
  import { PurchaseFlowRoutes } from '@/shared/types/router.types'
  import { VisitorType } from '@/shared/types/store.types'
  import { getDateWithoutTime, newVisitor, setFormatDate } from '@/shared/utils'
  import includes from '@mocks/includes'
  import notIncludes from '@mocks/notIncludes'
  import { addDays } from 'date-fns'
  import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'

  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import { usePrepareInboundAddon } from '@/shared/composables/usePrepareInboundAddon'
  import AlertQC from '@qicgroupdoha/qic-connect-ds/components/AlertQC'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { isYoungerThan23 } from './QuoteWidget.helpers'
  import OoredooBanner from '@/widgets/InsuranceSuccessWidget/components/OoredooBanner/OoredooBanner.vue'
  import { useFeaturesStore } from '@/shared/store/feature'

  const emit = defineEmits<Emits>()

  const HealthCoverage = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/HealthCoverage/HealthCoverage.vue')
  )
  const InboundAddon = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/InboundAddon/InboundAddon.vue')
  )
  const QuoteCheckout = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/QuoteCheckout/QuoteCheckout.vue')
  )

  const CoversPopup = defineAsyncComponent(
    () => import('@/shared/components/rebranding/popups/CoversPopup.vue')
  )
  const VisitorForm = defineAsyncComponent(
    () => import('@/shared/components/rebranding/VisitorForm/VisitorForm.vue')
  )
  const QuoteInclusive = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/QuoteInclusive.vue')
  )
  const QuoteInsuredList = defineAsyncComponent(
    () => import('@/shared/components/rebranding/quote/QuoteInsuredList/QuoteInsuredList.vue')
  )

  interface Emits {
    (e: 'proceed', value?: string): void
    (e: 'load', callback: () => Promise<void>, requestId?: string, withLoader?: boolean): void
    (e: 'error', value: string): void
  }

  type ProceedPaymentType = {
    paymentMethod: number
    isGpayOrApplePay: boolean
  }

  const homeUrl = useHomeUrl()
  const visitorsStore = useVisitorsStore()
  const coreStore = useCoreStore()
  const router = useRouter()
  const { locale, t } = useI18n()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const { isInboundAddonEnabled, checkPrepareInboundAddon } = usePrepareInboundAddon()
  const featuresStore = useFeaturesStore()
  const manualSelectedInboundAddon = computed(() => visitorsStore.manualSelectedInboundAddon)

  const availablePaymentMethods = ref<PaymentType[]>([])
  const view = ref(viewsMap.QUOTE)
  const isLoading = ref(false)
  const currentVisitor = ref<VisitorType>(defaultVisitor)
  const emailIsValid = ref(true)
  const visitors = ref<VisitorType[]>([])
  const termsHref = ref('')
  const benefitsLink = ref('')
  const isCoversPopupShown = ref(false)
  const isInboundAddonSelected = ref(manualSelectedInboundAddon.value ?? false)
  const isLoadingInboundAddon = ref(false)
  const totalPriceWithInboundAddon = ref(null)
  const showInboundAddon = ref(false)
  const inboundPolicyExists = ref(false)
  const policyWordingInbound = ref('')
  const showAlertForPassportExpired = ref(false)
  const passportExpiredAlertText = ref('')
  const isInboundAddonAbTest = INBOUND_ADDON_KEYS.includes(coreStore.getAbValue(INBOUND_ADDON_CODE))

  const getQuote = computed(() => visitorsStore.getQuoteData)
  const getVisitorsPrice = computed(() => visitorsStore.getQuotePrice ?? 0)
  const getQuotePrice = computed(
    () => totalPriceWithInboundAddon.value ?? visitorsStore.getQuotePrice ?? 0
  )
  const allVisitors = computed(() => visitorsStore.visitorsList)
  const getEntryValue = computed(() => visitorsStore.getEntryValue)
  const visitDates = computed(() => visitorsStore.getVisitDates)
  const getSingleQuotePrice = computed(() =>
    getVisitorsPrice.value ? getVisitorsPrice.value / visitors.value.length : 0
  )
  const matchingList = computed(() =>
    visitorsStore.visitorsList.filter((v) => v.durationMatch !== 0)
  )
  const visitorsWithDurationMatch = computed(() =>
    visitors.value.filter((visitor) => visitor.durationMatch !== 0)
  )
  const entryType = visitorsStore.getAnalyticsEntryType
  const durations = computed(() => coreStore.durations)
  const editVisitorId = computed(() => visitorsStore.editVisitorId ?? currentVisitor.value.srNo)
  const isHolder = computed(
    () => currentVisitor.value && useIsHolder(currentVisitor.value.relationship)
  )
  const isEdit = computed(() => view.value === viewsMap.EDIT_VISITOR)
  const visitorsMap = computed(() => visitorsStore.visitorsMap)
  const nonMatchingList = computed(() => allVisitors.value.filter((v) => v.durationMatch === 0))
  const holderFullName = computed(
    () => `${visitorsStore.holder.firstName} ${visitorsStore.holder.lastName}`
  )

  const isShowInboundAddon = computed(
    () => isInboundAddonAbTest && showInboundAddon.value && isInboundAddonEnabled
  )

  const isPassportExpiredAndBlocked = computed(
    () => passportExpiredAlertText.value.length > 0 && showAlertForPassportExpired.value
  )

  const checkPassportExpired = (): boolean => {
    const currentDate = getDateWithoutTime(new Date()).getTime()
    const expiredVisitors: string[] = []

    matchingList.value.forEach((visitor) => {
      if (visitor.passportExpiryDate) {
        const expiryDate = getDateWithoutTime(new Date(visitor.passportExpiryDate)).getTime()
        if (expiryDate < currentDate) {
          const fullName = `${visitor.firstName} ${visitor.lastName}`.trim()
          expiredVisitors.push(fullName)
        }
      }
    })

    if (expiredVisitors.length > 0) {
      showAlertForPassportExpired.value = true
      passportExpiredAlertText.value = `${t('visitors.quote.passport_expired.alert')}${expiredVisitors.join(', ')}`
      return false
    } else {
      showAlertForPassportExpired.value = false
      passportExpiredAlertText.value = ''
      return true
    }
  }

  const formatDate = (val: Date) => setFormatDate(val, locale.value as LocaleType)
  const redirLink = (paymentMethod: number) => {
    window.location.href =
      paymentMethod === 0
        ? import.meta.env.VUE_APP_REDIRECT_CC
        : import.meta.env.VUE_APP_REDIRECT_DC
  }

  const pluggablePaySucceed = () => router.replace({ name: PurchaseFlowRoutes.INSURANCE_RESULT })
  const onNextPage = (isGpayOrApplePay: boolean, paymentMethod: number) =>
    isGpayOrApplePay ? pluggablePaySucceed() : redirLink(paymentMethod)

  const usdPrice = useConvertUsd(getQuotePrice.value)
  const getHoldersPass = () => {
    const array_pass: string[] = []
    visitors.value.forEach((visitor) =>
      array_pass.push(visitor.passport.substring(visitor.passport.length - 4))
    )
    return array_pass
  }
  const sendEventViewQuote = () => {
    gaService.sendViewQuote(
      entryType,
      allVisitors.value.length,
      getQuote.value.quote || '',
      coreStore.getInitialReferrer,
      getHoldersPass()
    )
  }

  const onProceed = async ({ paymentMethod, isGpayOrApplePay }: ProceedPaymentType) => {
    if (!checkPassportExpired()) return
    isLoading.value = true
    emit(
      'load',
      async () => {
        if (isInboundAddonSelected.value) {
          await visitorsStore
            .inboundAddonCheck(
              visitorsStore.holder.passport,
              visitDates.value.startDate,
              visitDates.value.tillDate
            )
            .catch(async (error) => {
              const errorCode = error?.response?.data?.errorCode
              inboundPolicyExists.value = errorCode === inboundCheckResponse
              if (inboundPolicyExists.value) {
                await onInboundAddonSelect()
              } else {
                emit('error', error)
              }
              isLoading.value = false
            })

          if (inboundPolicyExists.value) return
        }
        const quoteNo = getQuote.value.quote ? getQuote.value.quote : ''
        const array_pass = getHoldersPass()

        localStorage.setItem('user-payload.quoteno', quoteNo)
        localStorage.setItem('user-payload.price', getQuotePrice.value.toString())
        localStorage.setItem('paymentMethod', paymentMethods[paymentMethod])

        localStorage.setItem(
          'user-payload.uninsuredUsersCount',
          nonMatchingList.value.length.toString()
        )
        localStorage.removeItem('user-payload.blockRemoveTravellers')
        gaService.sendPurchaseStart(
          entryType,
          quoteNo,
          usdPrice,
          paymentMethods[paymentMethod],
          coreStore.getInitialReferrer,
          visitors.value.length,
          array_pass,
          isInboundAddonSelected.value,
          visitorsStore.inboundPrice || 0
        )
        onNextPage(isGpayOrApplePay, paymentMethod)
      },
      'on-proceed-quote',
      false
    )
  }

  const getTotalPriceApplePay = () => getQuotePrice.value
  const redirectToMainPage = () => {
    window.location.href = homeUrl
  }
  const getTillDate = () => {
    const entry = getEntryById(getEntryValue.value, durations.value)

    if (!entry) {
      return '-'
    }

    return typeof entry.duration === 'number'
      ? formatDate(addDays(visitDates.value.startDate, entry.duration - 1))
      : '-'
  }
  const dates = computed(() => ({
    startDate: formatDate(visitDates.value.startDate),
    tillDate: getTillDate(),
  }))

  const getPreapprovalVisitor = (visitor: VisitorType): Promise<VisitorType> =>
    visitorsStore
      .getPreapprovalVisa(visitor)
      .then((data) => ({
        ...visitor,
        ...data,
      }))
      .catch(() => ({
        ...visitor,
        visaValidityDate: undefined,
        policyDuration: visitorsStore.holder.policyDuration || visitorsStore.entryValue,
      }))

  const sendEventOnSave = (event: string, page: string, visitor: VisitorType) => {
    gaService.sendEventOnChangePerson(
      event,
      entryType,
      page,
      visitor.nationalityName || '',
      visitor.relationshipName,
      visitor.passport.substring(visitor.passport.length - 4),
      !!visitor.visaValidityDate
    )
  }

  const sendEventOnAddVisitor = sendEventOnSave.bind(null, 'added_new_person', 'add_person')
  const sendEventOnEditVisitor = sendEventOnSave.bind(null, 'click_proceed', 'update_information')

  const getInboundAddonQuoteForActions = async () => {
    if (isInboundAddonAbTest && isInboundAddonEnabled) {
      return await visitorsStore
        .getInboundAddonQuote()
        .then(async () => {
          if (
            visitorsStore.inboundPrice &&
            visitorsStore.inboundPrice > 0 &&
            visitorsStore.inboundQuoteNumber
          ) {
            showInboundAddon.value = true
            gaService.sendViewInboundAddon(getQuote.value.quote || '')
            if (
              (manualSelectedInboundAddon.value !== null && !manualSelectedInboundAddon.value) ||
              (isYoungerThan23(visitorsStore.holder.birthDate ?? '') &&
                manualSelectedInboundAddon.value === null)
            ) {
              totalPriceWithInboundAddon.value = null
              isInboundAddonSelected.value = false
              await visitorsStore.updateQuote()
              return
            }
            await visitorsStore
              .updateQuote('addon')
              ?.then((res) => {
                totalPriceWithInboundAddon.value = res?.data?.totalPrice ?? null
                isInboundAddonSelected.value = !!totalPriceWithInboundAddon.value
              })
              .catch(() => {
                totalPriceWithInboundAddon.value = null
                isInboundAddonSelected.value = false
                showInboundAddon.value = false
              })
          }
        })
        .catch(() => {
          totalPriceWithInboundAddon.value = null
          isInboundAddonSelected.value = false
          showInboundAddon.value = false
        })
    }
    return null
  }

  const saveVisitor = (visitorInfo: VisitorType) => {
    emit(
      'load',
      async () => {
        isLoading.value = true
        const sendEvent =
          view.value === viewsMap.ADD_VISITOR ? sendEventOnAddVisitor : sendEventOnEditVisitor
        let visitor = visitorInfo
        let isError = false
        localStorage.setItem(STATE_LIST_INS_KEY, view.value)

        const isRedirectTravelDuration = () =>
          useIsHolder(visitor.relationship) && !!visitor.visaValidityDate

        visitor = await getPreapprovalVisitor(visitor)
        await visitorsStore
          .updateVisitor(newVisitor(visitor))
          .then(async () => {
            sendEvent(visitor)
            await visitorsStore.getTravellers()

            if (isRedirectTravelDuration()) {
              emit('proceed', PurchaseFlowRoutes.TRAVEL_DURATION)
            }

            currentVisitor.value = defaultVisitor
            view.value = viewsMap.QUOTE
            localStorage.removeItem(STATE_LIST_INS_KEY)
            setTimeout(useScrollToTop, 0)
          })
          .catch(async (error) => {
            isError = true
            const message = error?.response?.data.errorMessage
            emailIsValid.value = message !== emailResponse
            await visitorsStore.getTravellers()
          })
          .finally(() => {
            isLoading.value = false
          })

        if (isError || isRedirectTravelDuration()) return

        Promise.all([
          await visitorsStore.getQuote(matchingList.value.length),
          await visitorsStore.updateQuote(),
          await visitorsStore.getQuoteDetails().then((data) => {
            visitors.value = data
            if (
              !Object.prototype.hasOwnProperty.call(getQuote.value, 'schemes') ||
              !visitors.value[0]?.firstName
            ) {
              return redirectToMainPage()
            } else {
              sendEventViewQuote()
            }
          }),
        ])
        if (useIsHolder(visitor.relationship)) {
          if (checkPrepareInboundAddon(visitor, visitorsStore.entryValue)) {
            await visitorsStore
              .inboundAddonCheck(
                visitor.passport,
                visitDates.value.startDate,
                visitDates.value.tillDate
              )
              .then(async (res) => {
                visitorsStore.setInboundAddon(res?.success || false)
                if (!visitorsStore.isInboundAddon) {
                  showInboundAddon.value = false
                  totalPriceWithInboundAddon.value = null
                  return
                }
                await getInboundAddonQuoteForActions()
              })
              .catch((error) => {
                const errorCode = error?.response?.data?.errorCode
                inboundPolicyExists.value = errorCode === inboundCheckResponse
                if (!inboundPolicyExists.value) {
                  emit('error', error)
                }
              })
          } else {
            visitorsStore.setInboundAddon(false)
            showInboundAddon.value = false
            totalPriceWithInboundAddon.value = null
          }
        } else {
          await getInboundAddonQuoteForActions()
        }
      },
      'quote-save-visitor',
      !isLoading.value
    )
  }

  const addVisitor = () => {
    currentVisitor.value = newVisitor(defaultVisitor)
    currentVisitor.value.nationality = visitorsStore.holder.nationality
    view.value = viewsMap.ADD_VISITOR
    localStorage.setItem(STATE_LIST_INS_KEY, view.value)
    gaService.sendAddPerson(
      'your_quote',
      entryType,
      allVisitors.value.length,
      getQuote.value.quote || '',
      coreStore.getInitialReferrer
    )
  }

  const editVisitor = ({ srNo }: { srNo: VisitorType['srNo'] }) => {
    visitorsStore.setEditVisitorId(srNo)
    if (editVisitorId.value) {
      const currVisitor = visitorsMap.value.get(editVisitorId.value)
      currentVisitor.value = currVisitor || defaultVisitor
      view.value = viewsMap.EDIT_VISITOR
      localStorage.setItem(STATE_LIST_INS_KEY, view.value)
      gaService.sendEditPerson(
        'your_quote',
        entryType,
        allVisitors.value.length,
        getQuote.value.quote || '',
        coreStore.getInitialReferrer,
        useIsHolder(currentVisitor.value.relationship) ? 'main' : 'additional',
        currentVisitor.value.passport.substring(currentVisitor.value.passport.length - 4),
        !!currentVisitor.value.visaValidityDate
      )
    }
  }

  const deleteVisitor = (visitor: VisitorType) => {
    emit(
      'load',
      async () => {
        showAlertForPassportExpired.value = false
        await Promise.all([
          await visitorsStore.removeTraveller(visitor).then(() => {
            gaService.sendRemoveVisitor(
              'delete_person',
              view.value,
              visitor.nationalityName || '',
              visitor.relationshipName,
              !!visitor.visaValidityDate,
              visitor.passport.substring(visitor.passport.length - 4),
              {
                btn_title: 'remove',
              }
            )
          }),
          (view.value = viewsMap.QUOTE),
          await visitorsStore.getTravellers(),
          await visitorsStore.getQuote(matchingList.value.length),
          await visitorsStore.updateQuote(),
          await visitorsStore.getQuoteDetails().then((data) => {
            visitors.value = data
            if (
              !Object.prototype.hasOwnProperty.call(getQuote.value, 'schemes') ||
              !visitors.value[0]?.firstName
            ) {
              return redirectToMainPage()
            } else {
              sendEventViewQuote()
            }
          }),
          await getInboundAddonQuoteForActions(),
        ])
      },
      'quote-delete-visitor'
    )
  }

  const sendExpandEvent = (visitor: VisitorType) => {
    gaService.sendExpandEventByQuote(
      entryType,
      allVisitors.value.length,
      getQuote.value.quote || '',
      coreStore.getInitialReferrer,
      useIsHolder(visitor.relationship) ? 'main' : 'additional',
      visitor.passport.substring(visitor.passport.length - 4),
      !!visitor.visaValidityDate
    )
  }

  const sendRemoveVisitor = (visitor: VisitorType, action: 'open' | 'close') => {
    const analyticsParams = action === 'close' ? { btn_title: 'cancel' } : {}

    const eventName = action === 'open' ? 'delete_person' : 'click_btn'
    const lastFourDigits = visitor.passport.slice(-4)

    gaService.sendRemoveVisitor(
      eventName,
      view.value,
      visitor.relationshipName || '',
      visitor.nationalityName || '',
      !!visitor.visaValidityDate,
      lastFourDigits,
      analyticsParams
    )
  }

  const onInboundAddonSelect = async () => {
    emit(
      'load',
      async () => {
        isLoadingInboundAddon.value = true
        isInboundAddonSelected.value = !isInboundAddonSelected.value
        visitorsStore.setManualSelectedInboundAddon(isInboundAddonSelected.value)
        if (isInboundAddonSelected.value) {
          await visitorsStore
            .updateQuote('addon')
            ?.then((res) => {
              totalPriceWithInboundAddon.value = res?.data?.totalPrice ?? null
            })
            .catch(async () => {
              totalPriceWithInboundAddon.value = null
              isInboundAddonSelected.value = false
              await visitorsStore.updateQuote()
            })
        } else {
          await visitorsStore.updateQuote()
          totalPriceWithInboundAddon.value = null
        }
        gaService.sendToggleEventInboundAddon(
          getQuote.value.quote || '',
          visitorsStore.inboundPrice || 0,
          isInboundAddonSelected.value
        )
        isLoadingInboundAddon.value = false
      },
      'quote-inbound-addon-select',
      false
    )
  }

  const sendLearnMoreEvent = (coverName: string) =>
    gaService.sendQuoteLearnMore(getQuote.value.quote || '', coverName)

  const checksInfo = async () => {
    Promise.all([
      await visitorsStore.getTravellers(),
      await visitorsStore.getQuote(matchingList.value.length),
      await visitorsStore.updateQuote(),
      await coreStore.getDurations(),
      await coreStore.loadStoreData(),
      await visitorsStore.getQuoteDetails().then((data) => {
        visitors.value = data
        if (
          !Object.prototype.hasOwnProperty.call(getQuote.value, 'schemes') ||
          !visitors.value[0]?.firstName
        ) {
          return redirectToMainPage()
        } else {
          sendEventViewQuote()
        }
        sendEventViewQuote()
      }),
      (availablePaymentMethods.value = await coreStore.getPaymentOptions(
        String(getQuote.value.quote)
      )),
    ])

    if (visitorsStore.isInboundAddon && isInboundAddonEnabled) {
      await visitorsStore
        .getInboundAddonQuote()
        .then(async () => {
          if (
            visitorsStore.inboundPrice &&
            visitorsStore.inboundPrice > 0 &&
            visitorsStore.inboundQuoteNumber
          ) {
            showInboundAddon.value = true
            gaService.sendViewInboundAddon(getQuote.value.quote || '')
            if (
              (manualSelectedInboundAddon.value !== null && !manualSelectedInboundAddon.value) ||
              (isYoungerThan23(visitorsStore.holder.birthDate ?? '') &&
                manualSelectedInboundAddon.value === null)
            ) {
              totalPriceWithInboundAddon.value = null
              isInboundAddonSelected.value = false
              return
            }

            await visitorsStore
              .updateQuote('addon')
              ?.then((res) => {
                totalPriceWithInboundAddon.value = res?.data?.totalPrice ?? null
                isInboundAddonSelected.value = !!totalPriceWithInboundAddon.value
              })
              .catch(() => {
                totalPriceWithInboundAddon.value = null
                isInboundAddonSelected.value = false
              })
          }
        })
        .catch(() => {
          showInboundAddon.value = false
          totalPriceWithInboundAddon.value = null
        })
    } else {
      totalPriceWithInboundAddon.value = null
      isInboundAddonSelected.value = false
    }

    const statePage = localStorage.getItem(STATE_LIST_INS_KEY)

    if (statePage && (statePage === viewsMap.EDIT_VISITOR || statePage === viewsMap.ADD_VISITOR)) {
      const currVisitor = visitorsMap.value.get(editVisitorId.value)
      if (statePage === viewsMap.ADD_VISITOR) {
        currentVisitor.value = defaultVisitor
      } else {
        currentVisitor.value = currVisitor ? currVisitor : defaultVisitor
      }
      view.value = statePage
    }
  }

  coreStore.setStopBack(true)
  coreStore.setBackCallback(() => {
    if (view.value === viewsMap.QUOTE) {
      router.go(-1)
    } else {
      localStorage.removeItem(STATE_LIST_INS_KEY)
      view.value = viewsMap.QUOTE
    }
  })

  onMounted(async () => {
    emit(
      'load',
      async () => {
        await checksInfo()
        termsHref.value = await useApiNew().commonSeervice.getTermsAndConditionsLink()
        benefitsLink.value = await useApiNew().commonSeervice.getBenefitsLink()
        if (isShowInboundAddon.value) {
          policyWordingInbound.value = await useApiNew().commonSeervice.getPolicyWordingInbound()
        }
        onPerformanceCheckEnd()
      },
      'quote-mounted'
    )
  })

  onUnmounted(() => {
    coreStore.setStopBack(false)
  })

  watch(view, (newVal) => {
    if (newVal === viewsMap.QUOTE) {
      showAlertForPassportExpired.value = false
    }
  })
</script>

<template>
  <div class="quote-widget">
    <template v-if="view === viewsMap.QUOTE">
      <FlowCard :title="$t('qlm_quote_header')" :with-button="false" class="quote-form flow">
        <div class="quote-dates text-medium">
          <div class="quote-dates__inner">
            {{ $t('policy_date') }} {{ dates.startDate }} - {{ dates.tillDate }}
          </div>
        </div>

        <div class="quote">
          <QuoteInsuredList
            :visitors="allVisitors"
            @add="addVisitor"
            @edit="editVisitor"
            @delete="deleteVisitor"
            @expand="sendExpandEvent"
            @open-remove-window-event="sendRemoveVisitor($event, 'open')"
            @close-remove-window-event="sendRemoveVisitor($event, 'close')"
          />
        </div>
      </FlowCard>

      <HealthCoverage
        v-if="isShowInboundAddon"
        :price="getSingleQuotePrice"
        :is-one-person="visitorsWithDurationMatch.length === 1"
        :policy-wording="termsHref"
        :quote-number="getQuote.quote || ''"
        :benefits-link="benefitsLink"
        @on-learn-more-click="sendLearnMoreEvent('health_coverage')"
      />

      <InboundAddon
        v-if="isShowInboundAddon"
        :price="visitorsStore.inboundPrice || 0"
        :is-loading="isLoadingInboundAddon"
        :is-selected="isInboundAddonSelected"
        :is-one-person="visitorsWithDurationMatch.length === 1"
        :is-warning="inboundPolicyExists"
        :full-name="holderFullName"
        :policy-wording="policyWordingInbound"
        :quote-number="getQuote.quote || ''"
        @on-select="onInboundAddonSelect"
        @on-learn-more-click="sendLearnMoreEvent('inbound_coverage')"
      />

      <FlowCard v-if="!isShowInboundAddon" :with-button="false" class="quote-form flow">
        <QuoteInclusive
          :includes="includes"
          :quote-id="getQuote.quote"
          @show:covers="isCoversPopupShown = true"
        />
      </FlowCard>

      <OoredooBanner
        v-if="featuresStore.isOoredooEnabled"
        :policy-id="getQuote.quote || ''"
        :with-button="false"
        :title="$t('ooredoo_block.quote.header')"
        :descriptions="[
          $t('ooredoo_block.quote.description_1'),
          $t('ooredoo_block.quote.description_2'),
          $t('ooredoo_block.quote.description_3'),
        ]"
        :wrap-title="false"
      />

      <FlowCard :with-button="false" class="quote-form flow quote-form--payment">
        <h3 class="heading-6 payment-title">{{ $t('qic_quote_payment_select') }}</h3>
        <QuoteCheckout
          :person-count="visitorsWithDurationMatch.length"
          :quote-inclusive-price="getSingleQuotePrice"
          :quote-number="getQuote.quote || ''"
          :quote-total-price="getQuotePrice"
          :is-inbound-addon="isInboundAddonAbTest"
          :is-inbound-addon-selected="isInboundAddonSelected"
          :is-loading="isLoading"
          :loading-price="isLoadingInboundAddon"
          :inbound-addon-price="visitorsStore.inboundPrice || 0"
          :terms-href="termsHref"
          :available-payment-methods="availablePaymentMethods"
          :get-apple-pay-total-callback="getTotalPriceApplePay"
          :validate-before-payment="checkPassportExpired"
          @on-payment="onProceed"
          @error="emit('error', $event)"
        />
      </FlowCard>

      <!-- @vue-ignore -->
      <ModalQC
        v-if="isCoversPopupShown"
        :title="$t('covers_popup.title')"
        hide-controls
        position="side"
        :locale="locale"
        @close="isCoversPopupShown = false"
      >
        <template #content>
          <CoversPopup :includes="includes" :not-includes="notIncludes" :documents="documents" />
        </template>
      </ModalQC>
      <AlertQC
        v-if="isPassportExpiredAndBlocked"
        class="alert-passport-expired"
        :subtitle="passportExpiredAlertText"
        variant="error"
        closable
        @close="showAlertForPassportExpired = false"
      />
    </template>

    <VisitorForm
      v-if="view === viewsMap.ADD_VISITOR || view === viewsMap.EDIT_VISITOR"
      :key="editVisitorId"
      :visitor="currentVisitor"
      :is-holder="isHolder"
      :is-edit="isEdit"
      :is-loading="isLoading"
      :email-is-valid="emailIsValid"
      @save="saveVisitor"
      @delete="deleteVisitor"
      @set-email-is-valid="emailIsValid = true"
      @open-remove-window-event="sendRemoveVisitor($event, 'open')"
      @close-remove-window-event="sendRemoveVisitor($event, 'close')"
    />
  </div>
</template>

<style lang="scss" scoped>
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';
  .quote-form {
    gap: var(--spacing-4);
  }

  .quote-widget {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);
  }

  .quote-dates {
    margin: 0 auto;
  }

  .quote-dates__inner {
    color: var(--colorTxtSrfPrimary);
    white-space: nowrap;
  }

  .quote-widget {
    width: 100%;
  }

  .payment-title {
    padding-block: var(--spacing-2);
    margin-bottom: var(--spacing-3);
  }

  .quote-form--payment {
    gap: var(--spacing-5);
  }

  .alert-passport-expired {
    position: fixed;
    top: var(--spacing-9);
    inset-inline-end: var(--spacing-8);
    max-width: 366px;
    z-index: 1000;
    :deep(.ds-alert__content) {
      .ds-alert__text-subtitle {
        @include typography.text-medium;
      }
    }
    @media (max-width: 568px) {
      top: var(--spacing-4);
      inset-inline: var(--spacing-4);
      max-width: 100%;
    }
  }
</style>
